import { useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch, useLocation } from "react-router-dom";
import Layout from "../../components/Layout";
import {
  ListEmptyState,
  ListTableLoading,
} from "../../components/ListSearchFilters";
import { ListPagination } from "../../components/ListPagination";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import { useAuth } from "../../hooks/auth";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  parseBinaryFilter,
  shouldSyncSearchToFirstPage,
} from "../../utils/listFilterQuery";
import { DocumentsListTable, DocumentListRow } from "./DocumentsListTable";
import { DocumentsListFilters } from "./DocumentsListFilters";
import {
  RentProcessListItem,
  buildDocumentsPath,
  mapProcessToRow,
} from "./documentsListQuery";
import DocumentsModal from "./components/DocumentsModal/DocumentsModal";

interface IParams {
  page: string;
}

interface IPaginatedResponse<T> {
  data: T[];
  last_page: number;
}

type LocationState = {
  rentProcessId?: number;
};

export default function Documents() {
  const location = useLocation<LocationState>();
  const history = useHistory();
  const { params } = useRouteMatch<IParams>();
  const { access_token, dateToExpires, user } = useAuth();

  const currentPage = Number(params.page) || 1;
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const urlSearch = (query.get("search") ?? "").trim();
  const hasDocuments = parseBinaryFilter(query.get("has_documents"));
  const hasDocumentLink = parseBinaryFilter(query.get("has_document_link"));

  const [selectedRentProcessId, setSelectedRentProcessId] = useState<number | null>(
    null
  );
  const [rows, setRows] = useState<DocumentListRow[]>([]);
  const [lastPage, setLastPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(urlSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(urlSearch);

  useEffect(() => {
    const rpId = location.state?.rentProcessId;
    if (rpId) {
      setSelectedRentProcessId(rpId);
      history.replace(location.pathname + location.search, {});
    }
  }, [history, location.pathname, location.search, location.state?.rentProcessId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    if (!shouldSyncSearchToFirstPage(debouncedSearch, urlSearch)) return;

    const nextUrl = buildDocumentsPath(
      1,
      debouncedSearch,
      hasDocuments,
      hasDocumentLink
    );
    const currentUrl = `${history.location.pathname}${history.location.search}`;
    if (currentUrl !== nextUrl) history.push(nextUrl);
  }, [debouncedSearch, hasDocuments, hasDocumentLink, urlSearch, history]);

  useEffect(() => {
    if (!user) {
      history.push("/");
      return;
    }

    if (shouldSyncSearchToFirstPage(debouncedSearch, urlSearch)) return;

    window.scrollTo(0, 0);

    async function loadDocuments() {
      setLoading(true);
      try {
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token || access_token}`;

        const response = await api.get<IPaginatedResponse<RentProcessListItem>>(
          "/api/adm/documents/files",
          {
            params: {
              page: currentPage,
              ...(debouncedSearch ? { search: debouncedSearch } : {}),
              ...(hasDocuments ? { has_documents: hasDocuments } : {}),
              ...(hasDocumentLink ? { has_document_link: hasDocumentLink } : {}),
            },
          }
        );

        setRows(response.data.data.map(mapProcessToRow));
        setLastPage(Number(response.data.last_page) || 0);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    loadDocuments();
  }, [
    history,
    access_token,
    dateToExpires,
    user,
    currentPage,
    debouncedSearch,
    urlSearch,
    hasDocuments,
    hasDocumentLink,
  ]);

  return (
    <Layout>
      {selectedRentProcessId && (
        <DocumentsModal
          rentProcessId={selectedRentProcessId}
          onClose={() => setSelectedRentProcessId(null)}
        />
      )}

      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-2 py-4 font-[inherit] md:px-3 md:py-5">
        <div className="mb-4">
          <h3 className="text-2xl font-bold text-slate-800">Documentos</h3>
          <p className="mt-1 text-sm text-slate-500">
            Consulte, pesquise e filtre os processos de documentação.
          </p>
        </div>

        <DocumentsListFilters
          searchTerm={searchTerm}
          hasDocuments={hasDocuments}
          hasDocumentLink={hasDocumentLink}
          onSearchChange={setSearchTerm}
          onHasDocumentsChange={(value) => {
            history.push(
              buildDocumentsPath(1, debouncedSearch, value, hasDocumentLink)
            );
          }}
          onHasDocumentLinkChange={(value) => {
            history.push(
              buildDocumentsPath(1, debouncedSearch, hasDocuments, value)
            );
          }}
        />

        {loading ? (
          <ListTableLoading />
        ) : rows.length === 0 ? (
          <ListEmptyState message="Nenhum processo encontrado para esta busca." />
        ) : (
          <DocumentsListTable
            rows={rows}
            onOpenDocuments={setSelectedRentProcessId}
          />
        )}

        <ListPagination
          currentPage={currentPage}
          totalPages={lastPage}
          onPageChange={(page) =>
            history.push(
              buildDocumentsPath(
                page,
                debouncedSearch,
                hasDocuments,
                hasDocumentLink
              )
            )
          }
        />
      </div>
    </Layout>
  );
}
